import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Alert, Card } from 'react-bootstrap';
import { apiService } from '../services/api';
import { useConfirmation } from '../contexts/ConfirmationContext';
import './ResetPasswordPage.scss';

const ResetPasswordPage: React.FC = () => {
  const { uid, token } = useParams<{ uid: string; token: string }>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);
  const navigate = useNavigate();
  const { alert } = useConfirmation();

  useEffect(() => {
    if (!uid || !token) {
      setIsValidToken(false);
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [uid, token]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (!uid || !token) {
      setError('Invalid reset link');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await apiService.auth.confirmPasswordReset(uid, token, password, confirmPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login/');
      }, 3000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to reset password. Please try again.';
      setError(errorMessage);
      setIsValidToken(false);
      alert({
        message: errorMessage,
        variant: 'danger',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isValidToken && !success) {
    return (
      <div className="reset-password-page">
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <Card className="shadow-sm">
                <Card.Body className="p-4">
                  <div className="text-center">
                    <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                    <h2 className="mb-3">Invalid Reset Link</h2>
                    <Alert variant="danger" className="mb-4">
                      {error || 'The password reset link is invalid or has expired. Please request a new password reset.'}
                    </Alert>
                    <Link to="/forgot-password/" className="btn btn-primary me-2">
                      <i className="fas fa-redo me-2"></i>
                      Request New Reset Link
                    </Link>
                    <Link to="/login/" className="btn btn-outline-secondary">
                      <i className="fas fa-arrow-left me-2"></i>
                      Back to Login
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <Card className="shadow-sm">
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <i className="fas fa-lock fa-3x text-primary mb-3"></i>
                  <h2 className="mb-2">Reset Your Password</h2>
                  <p className="text-muted">
                    Enter your new password below.
                  </p>
                </div>

                {success ? (
                  <div className="text-center">
                    <Alert variant="success" className="mb-4">
                      <i className="fas fa-check-circle me-2"></i>
                      Password has been reset successfully! Redirecting to login...
                    </Alert>
                    <Link to="/login/" className="btn btn-primary">
                      <i className="fas fa-sign-in-alt me-2"></i>
                      Go to Login
                    </Link>
                  </div>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    {error && (
                      <Alert variant="danger" dismissible onClose={() => setError('')}>
                        {error}
                      </Alert>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isSubmitting}
                        required
                        minLength={6}
                      />
                      <Form.Text className="text-muted">
                        Password must be at least 6 characters long
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={isSubmitting}
                        required
                        minLength={6}
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      variant="primary"
                      className="w-100 mb-3"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Resetting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-key me-2"></i>
                          Reset Password
                        </>
                      )}
                    </Button>

                    <div className="text-center">
                      <Link to="/login/" className="text-decoration-none">
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Login
                      </Link>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;

